import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import MaterialTable from "material-table";
import { IconButton } from "@material-ui/core";
import Icon from "@jll/icon";

import {
  tableStyle,
  tableOptions,
  tableIcons,
} from "../../../../Component/Table/tableCommonStyles";
import Tooltip from "@material-ui/core/Tooltip";
import styles from "./styles";
import { Link } from "react-router-dom";
import { RecordIcon, ScoreIndicator } from "../../../../Component";
import { getAttributeValue } from "../../../../Utilities/Functions";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(styles);

const AssociationsTable = (props) => {
  const {
    associations = [],
    onRecordView,
    canUpdateAssociation,
    canRemoveAssociation,
    onRecordDelete,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    const tableData = [];
    for (let association of associations) {
      const { entity, associations } = association;
      const {
        id: entityId,
        urn,
        entity_category: entityCategory,
        attributes = [],
        scores = [],
      } = entity || {};
      const attr = attributes.find((a) => a.name === "Full name");
      const attrValue = getAttributeValue(attr);
      const entityName = attrValue || urn;
      const kycScore = scores.find((s) => s.check_type === "KYC") || {};
      const amlScore = scores.find((s) => s.check_type === "AML") || {};
      tableData.push({
        entityId,
        entityCategory,
        urn,
        entityName,
        associations,
        kycScore: kycScore.accepted_score || kycScore.guided_score || "-",
        amlScore: amlScore.accepted_score || amlScore.guided_score || "-",
        kycExpired: kycScore.expired,
        amlExpired: amlScore.expired
      });
    }
    setTableData(tableData);
  }, [associations]);

  const viewAssociation = (rowData) => {
    if (onRecordView && typeof onRecordView === "function") {
      onRecordView(rowData);
    }
  };

  const deleteAssociation = (rowData) => {
    if (onRecordDelete && typeof onRecordDelete === "function") {
      onRecordDelete(rowData);
    }
  };

  return (
    <div className={classes.tableWrapper}>
      <MaterialTable
        style={tableStyle}
        icons={tableIcons}
        options={tableOptions}
        isLoading={false}
        data={tableData}
        localization={{
          header: {
            actions: t("labels.common.table.actions"),
          },
          body: {
            emptyDataSourceMessage: t("labels.common.table.noRecordsFound"),
          },
        }}
        columns={[
          {
            title: t("labels.record.associations.table.headers.recordName"),
            field: "entityName",
            render: (rowData) => {
              return (
                <Link
                  to={{ search: `?id=${rowData.entityId}` }}
                  target="_blank"
                >
                  {rowData.entityName}
                </Link>
              );
            },
          },
          {
            title: t("labels.record.associations.table.headers.recordType"),
            field: "recordType",
            cellStyle: {
              alignContent: "left",
            },
            render: (rowData) => {
              return (
                <RecordIcon entityCategory={rowData.entityCategory} t={t} />
              );
            },
          },
          {
            title: t("labels.record.associations.table.headers.associations"),
            field: "associations",
            render: (rowData) => {
              return rowData.associations.map((as) => {
                return <div key={as.id}>{as.associationName}</div>;
              });
            },
          },
          {
            title: t("labels.record.associations.table.headers.kycScore"),
            field: "kycScore",
            render: (rowData) => {
              return (
                <ScoreIndicator
                  score={rowData.kycScore}
                  expired={rowData.kycExpired}
                />
              );
            },
          },
          {
            title: t("labels.record.associations.table.headers.amlScore"),
            field: "amlScore",
            render: (rowData) => {
              return (
                <ScoreIndicator
                  score={rowData.amlScore}
                  expired={rowData.amlExpired}
                />
              );
            },
          },
        ]}
        actions={[
          canUpdateAssociation && {
            icon: "view",
            position: "row",
            onClick: (event, rowData) => viewAssociation(rowData),
          },
          canRemoveAssociation && {
            icon: "remove",
            position: "row",
            onClick: (event, rowData) => deleteAssociation(rowData),
          },
        ]}
        components={{
          Action: (props) => {
            switch (props.action.icon) {
              case "view":
                return (
                  <Tooltip
                    title={t("labels.record.associations.table.actions.view")}
                    placement="bottom"
                  >
                    <IconButton
                      onClick={(event) =>
                        props.action.onClick(event, props.data)
                      }
                    >
                      <Icon className="far fa-file-alt" />
                    </IconButton>
                  </Tooltip>
                );
              case "remove":
                return (
                  <Tooltip
                    title={t("labels.record.associations.table.actions.remove")}
                    placement="bottom"
                  >
                    <IconButton
                      onClick={(event) =>
                        props.action.onClick(event, props.data)
                      }
                    >
                      <Icon className="far fa-trash-alt" />
                    </IconButton>
                  </Tooltip>
                );
              default:
                return null;
            }
          },
        }}
      />
    </div>
  );
};

export default AssociationsTable;
